import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useLedgerReturns } from '@/api/hooks/useLedgerReturns';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Spinner } from '@/components/ui/Spinner';
import { DownloadMenu } from '@/components/ui/DownloadMenu';
import { FileUploadZone, ImportPreviewDialog, ValidationErrorList, ValidationError } from '@/components/import';
import { downloadReturnsTemplate } from '@/lib/api/export';
import { validateReturnsImport, processReturnsImport, ValidationResult } from '@/lib/api/import';
import type { LedgerDetailOutletContext } from '../LedgerDetailPage';
import { AddReturnForm, AddReturnFormHandle } from './AddReturnForm';
import { TypeFilter } from './TypeFilter';
import { ReturnsList } from './ReturnsList';
import type { LedgerReturnType } from '@/api/types';
import type { ColumnDefinition } from '@/lib/export';
import { isLedgerArchived } from '@/utils/ledger';
import { formatDateISO, formatTimestampToISO } from '@/utils/date';

/**
 * Column definitions for cash flow export.
 */
const CASHFLOW_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'dateFormatted', label: 'Date', format: 'date' },
    { key: 'type', label: 'Type' },
    { key: 'valueDecimal', label: 'Value', format: 'money' },
    { key: 'currency', label: 'Currency' },
    { key: 'label', label: 'Label' },
];

/**
 * Import state machine types
 */
type ImportState =
    | { status: 'idle' }
    | { status: 'validating' }
    | { status: 'preview'; result: ValidationResult; file: File }
    | { status: 'processing'; result: ValidationResult; file: File }
    | { status: 'error'; errors: ValidationError[] };

/**
 * Cash Flow tab component for displaying and managing ledger returns.
 * Features:
 * - Type filter (All/Inflows/Outflows) synced to URL query params
 * - Expandable "Add new return" form at top
 * - Infinite scroll list of returns
 * - Single inline edit mode (only one row editable at a time)
 * - Visual distinction between inflows (green) and outflows (red)
 * - Export CSV/JSON button to download returns
 * - Import CSV button to upload returns
 */
export function CashFlowTab() {
    const { id } = useParams<{ id: string }>();
    const ledgerId = Number(id);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get ledger data from parent context (avoids redundant API call)
    const { ledger, userLatestConfirmationDate } = useOutletContext<LedgerDetailOutletContext>();

    // Null safety: if ledger is not available, return null and let parent handle 404
    if (!ledger) return null;

    const confirmedUpdatedDate = ledger.confirmedUpdatedDate;

    // Redirect debt ledgers away from cash-flow tab (not supported for debts)
    useEffect(() => {
        if (ledger.type === 'debt') {
            navigate(`/ledgers/${ledgerId}`, { replace: true });
        }
    }, [ledger.type, ledgerId, navigate]);

    // Get type filter from URL query params
    const typeFromUrl = searchParams.get('type') as LedgerReturnType | null;
    const typeFilter = typeFromUrl === 'inflow' || typeFromUrl === 'outflow' ? typeFromUrl : null;

    // Ref for the add form to control its expansion
    const addFormRef = useRef<AddReturnFormHandle>(null);

    // Track which return is being edited (null = none)
    const [editingReturnId, setEditingReturnId] = useState<number | null>(null);

    // Import dialog and state
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const [importState, setImportState] = useState<ImportState>({ status: 'idle' });

    // Check if ledger is archived
    const isArchived = isLedgerArchived(ledger);

    // Fetch returns with infinite scroll and optional type filter
    const {
        data,
        isLoading,
        error,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        refetch,
    } = useLedgerReturns(ledgerId, { type: typeFilter });

    // Flatten pages into a single array of returns
    const returns = useMemo(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => page.items);
    }, [data?.pages]);

    // Export filename with date suffix
    const exportFilename = useMemo(() => {
        return `ledger-${ledgerId}-cashflow-${formatDateISO()}`;
    }, [ledgerId]);

    const handleTypeFilterChange = useCallback(
        (newType: LedgerReturnType | null) => {
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                if (newType) {
                    newParams.set('type', newType);
                } else {
                    newParams.delete('type');
                }
                return newParams;
            });
        },
        [setSearchParams]
    );

    const handleAddFormOpen = () => {
        // Close any editing row when add form opens
        setEditingReturnId(null);
    };

    const handleEditRow = (returnId: number) => {
        setEditingReturnId(returnId);
        // Close add form when editing a row
        addFormRef.current?.collapse();
    };

    const handleCancelEdit = () => {
        setEditingReturnId(null);
    };

    const handleSaveComplete = () => {
        setEditingReturnId(null);
    };

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    // Open import dialog
    const handleOpenImportDialog = useCallback(() => {
        setImportState({ status: 'idle' });
        setIsImportDialogOpen(true);
    }, []);

    // Close import dialog
    const handleCloseImportDialog = useCallback(() => {
        setIsImportDialogOpen(false);
        setImportState({ status: 'idle' });
    }, []);

    // Handle file selection for import
    const handleFileSelected = useCallback(async (file: File) => {
        setImportState({ status: 'validating' });

        try {
            const result = await validateReturnsImport(ledgerId, file);

            if (!result.isValid || result.errors.length > 0) {
                // Parse errors into ValidationError format
                const errors: ValidationError[] = result.errors.map((error, index) => {
                    // Try to extract row number from error message
                    const rowMatch = error.match(/Row (\d+)/);
                    const rowStr = rowMatch?.[1];
                    const row = rowStr ? parseInt(rowStr, 10) : index + 1;

                    // Try to extract field from error message
                    const fieldMatch = error.match(/^([A-Za-z]+):/);
                    const field = fieldMatch?.[1] ?? 'general';

                    return { row, field, message: error };
                });

                setImportState({ status: 'error', errors });
                return;
            }

            setImportState({ status: 'preview', result, file });
        } catch (err) {
            console.error('Validation failed:', err);
            toast.error('Failed to validate file. Please try again.');
            setImportState({ status: 'idle' });
        }
    }, [ledgerId]);

    // Handle import confirmation
    const handleConfirmImport = useCallback(async () => {
        if (importState.status !== 'preview') return;

        const { file, result } = importState;
        setImportState({ status: 'processing', result, file });

        try {
            const importResult = await processReturnsImport(ledgerId, file);

            if (importResult.success) {
                // Refresh returns list
                await refetch();

                // Close dialog and reset state
                setIsImportDialogOpen(false);
                setImportState({ status: 'idle' });

                // Show success toast
                toast.success(
                    `Successfully imported ${importResult.entitiesCreated.returns} cash flows. ${importResult.creditsDeducted} credits deducted.`
                );
            } else {
                toast.error('Import failed. Please try again.');
                setImportState({ status: 'idle' });
            }
        } catch (err) {
            console.error('Import failed:', err);
            toast.error('Import failed. Please try again.');
            setImportState({ status: 'idle' });
        }
    }, [importState, ledgerId, refetch]);

    // Handle cancel preview dialog
    const handleCancelImport = useCallback(() => {
        setImportState({ status: 'idle' });
    }, []);

    // Handle download example template
    const handleDownloadTemplate = useCallback(async () => {
        try {
            await downloadReturnsTemplate();
        } catch (err) {
            console.error('Download template failed:', err);
            toast.error('Failed to download template. Please try again.');
        }
    }, []);

    // Handle retry after error
    const handleRetryImport = useCallback(() => {
        setImportState({ status: 'idle' });
    }, []);

    // Check if dialog should be in preview/processing mode
    const isPreviewDialogOpen = importState.status === 'preview' || importState.status === 'processing';
    const isProcessing = importState.status === 'processing';

    // Don't render anything if this is a debt ledger (will redirect)
    if (ledger.type === 'debt') {
        return null;
    }

    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <div className="flex items-center space-x-2">
                    <Spinner />
                    <span className="text-gray-500 dark:text-gray-400">Loading returns...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300">
                    Failed to load returns. Please try again.
                </p>
            </div>
        );
    }

    // Check if there are no returns at all (empty state)
    const hasNoReturns = returns.length === 0 && !typeFilter;

    return (
        <div className="space-y-4">
            {/* Add new return form - auto-expanded when empty */}
            <AddReturnForm
                ref={addFormRef}
                ledgerId={ledgerId}
                userMaxValueDate={userLatestConfirmationDate ? formatTimestampToISO(userLatestConfirmationDate) : undefined}
                defaultCurrency={ledger.latestValue?.originalCurrency}
                onFormOpen={handleAddFormOpen}
                defaultExpanded={hasNoReturns}
            />

            {/* Type filter and Export/Import buttons */}
            <div className="flex items-center justify-between">
                {/* Type filter - only show when DB has returns (not based on filtered results) */}
                {!hasNoReturns ? (
                    <TypeFilter value={typeFilter} onChange={handleTypeFilterChange} />
                ) : (
                    <div />
                )}

                {/* Export/Import buttons - always visible */}
                <div className="flex items-center gap-1">
                    <DownloadMenu
                        data={returns}
                        columns={CASHFLOW_EXPORT_COLUMNS}
                        filename={exportFilename}
                        disabled={returns.length === 0}
                    />
                    <div className="relative group">
                        <button
                            type="button"
                            onClick={handleOpenImportDialog}
                            disabled={isArchived}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            aria-label="Import CSV"
                        >
                            <ArrowUpTrayIcon className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-sm shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                            {isArchived ? 'Cannot import to archived ledger' : 'Import CSV'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Returns list */}
            {returns.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {typeFilter
                        ? `No ${typeFilter}s recorded yet.`
                        : 'No cash flows recorded yet.'}
                </div>
            ) : (
                <ReturnsList
                    returns={returns}
                    ledgerId={ledgerId}
                    confirmedUpdatedDate={confirmedUpdatedDate}
                    editingReturnId={editingReturnId}
                    onEdit={handleEditRow}
                    onCancelEdit={handleCancelEdit}
                    onSaveComplete={handleSaveComplete}
                    hasNextPage={hasNextPage ?? false}
                    isFetchingNextPage={isFetchingNextPage}
                    onLoadMore={handleLoadMore}
                />
            )}

            {/* Import Dialog */}
            <Dialog
                open={isImportDialogOpen && !isPreviewDialogOpen}
                onClose={handleCloseImportDialog}
                title="Import Cash Flows"
                size="lg"
            >
                <div className="space-y-4">
                    {importState.status === 'error' ? (
                        /* Error State */
                        <div className="space-y-4">
                            <ValidationErrorList errors={importState.errors} />
                            <Button variant="secondary" onClick={handleRetryImport}>
                                Try Again
                            </Button>
                        </div>
                    ) : (
                        /* Normal/Validating State */
                        <>
                            <p className="text-gray-600 dark:text-gray-400">
                                Upload a CSV file to import returns. The CSV should have columns:
                                Date, Type (inflow/outflow), Value, Currency (and optionally Label).
                            </p>

                            <FileUploadZone
                                acceptedTypes={['.csv']}
                                maxSizeMB={5}
                                onFileSelected={handleFileSelected}
                                disabled={importState.status === 'validating'}
                            />

                            {importState.status === 'validating' && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Validating file...
                                </p>
                            )}

                            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={handleDownloadTemplate}
                                    className="text-sm text-accent hover:text-accent-hover underline"
                                >
                                    Download Example CSV
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Dialog>

            {/* Import Preview Dialog */}
            {isPreviewDialogOpen && (importState.status === 'preview' || importState.status === 'processing') && (
                <ImportPreviewDialog
                    open={isPreviewDialogOpen}
                    validationResult={importState.result}
                    onConfirm={handleConfirmImport}
                    onCancel={handleCancelImport}
                    isProcessing={isProcessing}
                />
            )}
        </div>
    );
}
